/**
 * Custom hook for managing algorithm execution - JavaScript Version
 * 
 * Provides:
 * - Algorithm execution with validation
 * - Loading states
 * - Error handling
 * - Result management
 */

import { useState, useCallback } from 'react';
import { calculateShortestPath as apiCalculateShortestPath } from '../services/APIService';

// Mock validation function (we'll implement this properly later)
const validateParameters = (nodes, edges, start, end) => {
  return {
    isValid: true,
    errors: [],
    warnings: []
  };
};

export const useAlgorithm = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [isStepByStep, setIsStepByStep] = useState(false);

  /**
   * Execute algorithm with given parameters
   */
  const runAlgorithm = useCallback(async (
    algorithmId, 
    nodes, 
    edges, 
    start, 
    end
  ) => {
    setIsLoading(true);
    setError(null);
    setResult(null);
    setCurrentStep(0);
    setIsStepByStep(false);

    try {
      // Validate parameters
      const validation = validateParameters(nodes, edges, start, end);
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '));
      }

      // Execute algorithm via backend API
      const requestData = {
        nodes: nodes,
        edges: edges,
        start: start,
        end: end,
        algorithm: algorithmId
      };
      
      console.log('Sending API request:', requestData);
      const apiResponse = await apiCalculateShortestPath(requestData);
      console.log('API response received:', apiResponse);
      console.log('API response steps:', apiResponse.steps);
      console.log('API response success:', apiResponse.success);
      
      // Transform API response to AlgorithmResult format
      const algorithmResult = {
        success: apiResponse.success,
        path: apiResponse.path || [],
        cost: typeof apiResponse.cost === 'string' ? -1 : apiResponse.cost,
        steps: (apiResponse.steps || []).map(step => ({
          step: step.step,
          current: step.current,
          distances: step.distances,
          visited: step.visited,
          queue: step.queue,
          neighbors_updated: step.neighbors_updated?.map(update => update.node) || [],
          final: step.final
        })),
        algorithm: apiResponse.algorithm,
        executionTime: apiResponse.execution_time || apiResponse.executionTime || 0
      };

      setResult(algorithmResult);
      setIsStepByStep(algorithmResult.steps.length > 0);
      
      console.log('Algorithm result processed:', algorithmResult);
      return algorithmResult;
      
    } catch (err) {
      console.error('Algorithm execution failed:', err);
      const errorMessage = err.message || 'Algorithm execution failed';
      setError(errorMessage);
      
      // Return failed result
      const failedResult = {
        success: false,
        path: [],
        cost: -1,
        steps: [],
        algorithm: algorithmId,
        executionTime: 0
      };
      
      setResult(failedResult);
      return failedResult;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Navigate to next step
   */
  const nextStep = useCallback(() => {
    if (result && result.steps && currentStep < result.steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  }, [result, currentStep]);

  /**
   * Navigate to previous step
   */
  const previousStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  /**
   * Jump to specific step
   */
  const goToStep = useCallback((stepIndex) => {
    if (result && result.steps && stepIndex >= 0 && stepIndex < result.steps.length) {
      setCurrentStep(stepIndex);
    }
  }, [result]);

  /**
   * Get current step data
   */
  const getCurrentStep = useCallback(() => {
    if (result && result.steps && result.steps[currentStep]) {
      return result.steps[currentStep];
    }
    return null;
  }, [result, currentStep]);

  /**
   * Reset algorithm state
   */
  const reset = useCallback(() => {
    setResult(null);
    setError(null);
    setCurrentStep(0);
    setIsStepByStep(false);
  }, []);

  return {
    // State
    isLoading,
    error,
    result,
    currentStep,
    isStepByStep,
    
    // Actions
    runAlgorithm,
    nextStep,
    previousStep,
    goToStep,
    getCurrentStep,
    reset
  };
};
